import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { CheckCircle2, Copy, FileText, Image as ImageIcon, Link2, Search, Share2, Sparkles } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";

/**
 * Internal Content Publishing Checklist.
 *
 * NOT for end-users. Marked noindex/nofollow + excluded from sitemap.
 * Lives at /content-checklist so Chris can pull it up before publishing
 * any new article, service page, or city page.
 *
 * Distinct from /launch-checklist — that one is the live infrastructure
 * dashboard (DNS, robots, sitemap pings). This one is the content workflow.
 */

export const Route = createFileRoute("/content-checklist")({
  head: () => ({
    meta: [
        { name: "description", content: "Internal content publishing checklist used by the Colorado Web Impressions editorial team to ensure SEO, accuracy, and brand standards on every post." },
      { title: "Content Publishing Checklist | Internal CWI" },
      {
        name: "description",
        content:
          "Internal content workflow: pre-publish, schema, image, internal link, and post-publish steps for every new page on coloradowebimpressions.com.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/content-checklist" }],
  }),
  component: ContentChecklistPage,
});

type ChecklistItem = { label: string; detail?: string };
type ChecklistGroup = {
  id: string;
  icon: typeof FileText;
  title: string;
  intro?: string;
  items: ChecklistItem[];
};

const GROUPS: ChecklistGroup[] = [
  {
    id: "links",
    icon: Link2,
    title: "4. Internal linking",
    intro: "This is the single highest-leverage thing for SEO that most agencies skip.",
    items: [
      { label: "At least 1 link to a money page (services, pricing, contact, free audit)." },
      { label: "At least 2 links to supporting articles in /learn (use related posts in ArticleLayout)." },
      { label: "At least 1 link to a parent topic landing (e.g. /learn → /learn/topic/seo)." },
      { label: "Anchor text is keyword-rich for the destination (NOT \"click here\" or \"read more\")." },
      { label: "Add 2 inbound internal links from existing pages to this new page after publishing." },
      { label: "No orphan pages: every page must be reachable from the homepage in 3 clicks or fewer." },
    ],
  },
  {
    id: "postpublish",
    icon: Share2,
    title: "5. Post-publish actions",
    intro: "Within 24 hours of publishing.",
    items: [
      { label: "Run /launch-checklist to confirm sitemap regenerated and the new URL is in sitemap.xml." },
      { label: "Submit the URL in Google Search Console → URL Inspection → Request indexing." },
      { label: "Submit in Bing Webmaster Tools → URL Submission." },
      { label: "Add an internal link from at least 2 existing related pages." },
      { label: "Share to LinkedIn (Chris's profile + company page) with a 2-3 sentence value-led caption." },
      { label: "Share to Facebook + X with the og:image showing." },
      { label: "Schedule a Google Business Profile post linking to it (use the GBP post calendar)." },
      { label: "If it's a city or service page: add it to the /sitemap human page nav." },
    ],
  },
  {
    id: "refresh",
    icon: Search,
    title: "6. Quarterly refresh",
    intro: "Run this loop every 90 days on top 20 traffic pages.",
    items: [
      { label: "Update any \"as of [year]\" or pricing references." },
      { label: "Add 2-3 fresh internal links from articles published in the last quarter." },
      { label: "Re-check Google Search Console for the page's top 10 queries — work those phrases in if missing." },
      { label: "Update dateModified in the article registry (src/lib/learningHub.ts)." },
      { label: "Re-submit to GSC URL Inspection after editing." },
    ],
  },
];

function ContentChecklistPage() {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  function copyGroup(group: ChecklistGroup) {
    const text = `${group.title}\n${group.intro ? group.intro + "\n" : ""}${group.items
      .map((i) => `- [ ] ${i.label}${i.detail ? `\n      ${i.detail}` : ""}`)
      .join("\n")}`;
    navigator.clipboard?.writeText(text).then(() => {
      setCopiedId(group.id);
      setTimeout(() => setCopiedId(null), 1600);
    });
  }

  function copyAll() {
    const text = GROUPS.map(
      (g) =>
        `${g.title}\n${g.intro ? g.intro + "\n" : ""}${g.items
          .map((i) => `- [ ] ${i.label}`)
          .join("\n")}`,
    ).join("\n\n");
    navigator.clipboard?.writeText(text).then(() => {
      setCopiedId("all");
      setTimeout(() => setCopiedId(null), 1600);
    });
  }

  const totalItems = GROUPS.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Internal tool"
        title="Content Publishing Checklist"
        description="The pre-flight checklist before any new page goes live on coloradowebimpressions.com. Not indexed. Built for Chris."
      />

      <section className="container mx-auto max-w-4xl px-4 py-12">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-card p-5">
          <div>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{totalItems} checks</span> across{" "}
              {GROUPS.length} stages. This page is{" "}
              <span className="font-mono text-xs">noindex, nofollow</span> and excluded from
              sitemap.xml.
            </p>
          </div>
          <button
            onClick={copyAll}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Copy className="h-4 w-4" />
            {copiedId === "all" ? "Copied!" : "Copy entire checklist"}
          </button>
        </div>

        <div className="space-y-8">
          {GROUPS.map((group) => {
            const Icon = group.icon;
            return (
              <article
                key={group.id}
                className="rounded-lg border border-border bg-card p-6 shadow-sm"
              >
                <header className="mb-4 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Icon className="mt-1 h-5 w-5 text-primary" aria-hidden />
                    <div>
                      <h2 className="text-xl font-bold text-foreground">{group.title}</h2>
                      {group.intro && (
                        <p className="mt-1 text-sm text-muted-foreground">{group.intro}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => copyGroup(group)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                    aria-label={`Copy ${group.title} as markdown`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {copiedId === group.id ? "Copied" : "Copy"}
                  </button>
                </header>

                <ul className="space-y-2.5">
                  {group.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                      <CheckCircle2
                        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden
                      />
                      <span className="text-foreground">
                        {item.label}
                        {item.detail && (
                          <span className="mt-0.5 block text-xs text-muted-foreground">
                            {item.detail}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        <div className="mt-12 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          <p className="font-semibold text-foreground">Reminder</p>
          <p className="mt-1">
            This checklist is the bare minimum. Skipping schema or internal links is the most
            common reason new pages take 3-6 months to rank instead of 30-60 days.
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}
